import { data, Form, Link, useSearchParams } from 'react-router'
import { type Route } from './+types/index.ts'

export const meta: Route.MetaFunction = () => [
	{ title: 'Deed Transfers | EBHS Records' },
]

export async function loader({ request }: Route.LoaderArgs) {
	const { searchDeedTransfers } = await import(
		'#app/models/deed-transfer.server.ts'
	)
	const url = new URL(request.url)
	const term = url.searchParams.get('term')
	const year = url.searchParams.get('year')
	const liber = url.searchParams.get('liber')
	const reference = url.searchParams.get('reference')
	const page = parseInt(url.searchParams.get('page') ?? '0', 10)

	const hasFilters = term || year || liber || (reference && reference !== 'all')

	if (!hasFilters) {
		return data({
			results: [],
			totalCount: 0,
			page: 0,
			pageSize: 50,
			totalPages: 0,
		})
	}

	const searchResults = await searchDeedTransfers({
		term,
		year,
		liber,
		reference,
		page: isNaN(page) ? 0 : page,
	})

	return data(searchResults)
}

function formatDate(dateStr: string) {
	if (!dateStr || dateStr.length !== 8) return dateStr
	const y = dateStr.slice(0, 4)
	const m = dateStr.slice(4, 6)
	const d = dateStr.slice(6, 8)
	return `${m}/${d}/${y}`
}

export default function DeedSearchPage({
	loaderData,
}: Route.ComponentProps) {
	const { results, totalCount, page, totalPages } = loaderData
	const [searchParams] = useSearchParams()

	const term = searchParams.get('term') ?? ''
	const year = searchParams.get('year') ?? ''
	const liber = searchParams.get('liber') ?? ''
	const reference = searchParams.get('reference') ?? 'all'

	function pageLink(pageNum: number) {
		const params = new URLSearchParams(searchParams)
		params.set('page', String(pageNum))
		return `?${params.toString()}`
	}

	return (
		<div className="px-4 pb-8 pt-4">
			<div className="mx-auto max-w-6xl sm:px-6 lg:px-8">
				<header className="py-5">
					<h1 className="text-3xl font-bold leading-tight text-gray-900">
						Deed Transfers
					</h1>
					<p className="mt-2 text-lg text-gray-600">
						Search our index of deed transfer records.
					</p>
				</header>

				<Form method="get" className="rounded-lg bg-white p-6 shadow">
					<div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
						<div>
							<label
								htmlFor="term"
								className="block text-sm font-medium text-gray-700"
							>
								Last Name
							</label>
							<input
								type="text"
								id="term"
								name="term"
								defaultValue={term}
								placeholder="Search by last name"
								className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500"
							/>
						</div>
						<div>
							<label
								htmlFor="year"
								className="block text-sm font-medium text-gray-700"
							>
								Year
							</label>
							<input
								type="text"
								id="year"
								name="year"
								defaultValue={year}
								placeholder="e.g. 1964"
								className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500"
							/>
						</div>
						<div>
							<label
								htmlFor="liber"
								className="block text-sm font-medium text-gray-700"
							>
								Liber
							</label>
							<input
								type="text"
								id="liber"
								name="liber"
								defaultValue={liber}
								placeholder="e.g. 661"
								className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500"
							/>
						</div>
						<div>
							<label
								htmlFor="reference"
								className="block text-sm font-medium text-gray-700"
							>
								Reference
							</label>
							<select
								id="reference"
								name="reference"
								defaultValue={reference}
								className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500"
							>
								<option value="all">All</option>
								<option value="Seller">Seller</option>
								<option value="Buyer">Buyer</option>
								<option value="Noted">Noted</option>
							</select>
						</div>
					</div>
					<div className="mt-4">
						<button
							type="submit"
							className="inline-flex items-center rounded-md bg-ebhs-blue px-4 py-2 text-white shadow-sm hover:bg-ebhs-blue-dark focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
						>
							Search
						</button>
					</div>
				</Form>

				{totalCount > 0 ? (
					<div className="mt-6">
						<p className="mb-3 text-sm text-gray-600">
							{totalCount} result{totalCount !== 1 ? 's' : ''} found
							{totalPages > 1
								? ` — page ${page + 1} of ${totalPages}`
								: ''}
						</p>
						<div className="overflow-x-auto rounded-lg bg-white shadow">
							<table className="min-w-full divide-y divide-gray-300">
								<thead>
									<tr>
										<th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
											Name
										</th>
										<th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
											Year
										</th>
										<th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
											Date
										</th>
										<th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
											Liber/Page
										</th>
										<th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
											Reference
										</th>
										<th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
											PDF
										</th>
									</tr>
								</thead>
								<tbody className="divide-y divide-gray-200">
									{results.map((deed) => (
										<tr key={deed.id}>
											<td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900">
												<Link
													to={`/records/deeds/${deed.id}`}
													className="text-ebhs-blue underline hover:text-ebhs-blue-dark"
												>
													{deed.lastName}
													{deed.firstName
														? `, ${deed.firstName}`
														: ''}
													{deed.middleName
														? ` ${deed.middleName}`
														: ''}
												</Link>
											</td>
											<td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
												{deed.year ?? ''}
											</td>
											<td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
												{formatDate(deed.date)}
											</td>
											<td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
												{deed.liber}/{deed.page}
											</td>
											<td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
												{deed.reference}
											</td>
											<td className="whitespace-nowrap px-3 py-4 text-sm">
												<a
													href={`https://ebhs1838.s3.amazonaws.com/deeds/${deed.scanName}.pdf`}
													target="_blank"
													rel="noreferrer"
													className="text-ebhs-blue underline hover:text-ebhs-blue-dark"
												>
													View
												</a>
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>

						{totalPages > 1 ? (
							<div className="mt-4 flex items-center justify-between">
								{page > 0 ? (
									<Link
										to={pageLink(page - 1)}
										className="rounded-md bg-ebhs-blue px-4 py-2 text-white hover:bg-ebhs-blue-dark"
									>
										Previous
									</Link>
								) : (
									<span />
								)}
								{page < totalPages - 1 ? (
									<Link
										to={pageLink(page + 1)}
										className="rounded-md bg-ebhs-blue px-4 py-2 text-white hover:bg-ebhs-blue-dark"
									>
										Next
									</Link>
								) : (
									<span />
								)}
							</div>
						) : null}
					</div>
				) : searchParams.has('term') ||
				  searchParams.has('year') ||
				  searchParams.has('liber') ||
				  (searchParams.has('reference') &&
						searchParams.get('reference') !== 'all') ? (
					<p className="mt-6 text-gray-600">
						No results found. Try adjusting your search criteria.
					</p>
				) : null}
			</div>
		</div>
	)
}
